// Client-side mirror of dprEngine.js's computeDepreciation (+ the tranche
// half of computeSubsidy it depends on), trimmed to what the wizard's live
// "Depreciation Chart" preview needs. Kept here rather than imported from
// ca-backend since CRA's webpack build can't reach outside src/ — same
// pattern as dprLoanCalc.js / dprRmProcurementCalc.js.
const round2 = n => Math.round((n + Number.EPSILON) * 100) / 100;

// Which project year (1-based) an actual calendar date falls into, anchored
// to the case's Indian-FY base year (business_profile.baseFinancialYear —
// the same anchor fyLabel() uses). April–March FY, so Jan–Mar belongs to the
// FY that started the prior calendar year. Mirrors dprEngine.js's
// fyIndexForDate exactly.
export function fyIndexForDate(baseYear, dateStr) {
  if (!baseYear || !dateStr) return 1;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return 1;
  const fyStartYear = (d.getMonth() + 1) >= 4 ? d.getFullYear() : d.getFullYear() - 1;
  return Math.max(1, fyStartYear - Number(baseYear) + 1);
}

// Groups a subsidy's dated disbursements into { [year]: amount }, capped
// cumulatively at `sanctionedAmount` — mirrors dprEngine.js's computeSubsidy
// tranche logic, so the live preview matches the saved report's Asset
// Reduction timing exactly.
export function subsidyTranchesByYear(caseData, sanctionedAmount) {
  const sc = caseData.subsidy_config || {};
  if (sc.type === 'interest_subvention' || sanctionedAmount <= 0) return {};
  const baseFYYear = (caseData.business_profile || {}).baseFinancialYear;
  const byYear = {};
  let remaining = sanctionedAmount;
  if ((sc.disbursements || []).length) {
    sc.disbursements.slice()
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .forEach(d => {
        if (remaining <= 0) return;
        const yr = fyIndexForDate(baseFYYear, d.date);
        const applied = round2(Math.min(Number(d.amount) || 0, remaining));
        if (applied <= 0) return;
        byYear[yr] = round2((byYear[yr] || 0) + applied);
        remaining = round2(remaining - applied);
      });
  } else if (sc.timing === 'back_ended') {
    const yr = sc.disbursementYear || 1;
    const applied = round2(Math.min(sanctionedAmount, remaining));
    if (applied > 0) byYear[yr] = applied;
  }
  return byYear;
}

// Returns { byBlock: [{ name, rate, method, sourceKey, rows: [{ year,
// openingGross, addition, closingGross, depreciationAccounting,
// netBlockAccounting }] }] }.
export function computeDepreciationPreview(caseData, projectionYears) {
  const blocks = (caseData.fixed_assets || {}).blocks || [];
  const sc = caseData.subsidy_config || {};
  const cop = caseData.cost_of_project || {};
  const assetReduction = !!sc.grantedForAssetReporting && sc.accountingMethod === 'asset_reduction';
  const buildingTotal = (cop.buildingItems || []).reduce((s, i) => s + (i.amount || 0), 0);
  const pmTotal = (cop.plantMachineryItems || []).reduce((s, i) => s + (i.amount || 0), 0);
  const buildingRatio = (buildingTotal + pmTotal) > 0 ? buildingTotal / (buildingTotal + pmTotal) : 0;
  // Sanctioned amount (pct-of-eligible-cost, capped) — the block cards'
  // subsidyShare fields already sum to this when Granted, so reuse that sum
  // rather than re-deriving pct/cap here.
  const sanctionedAmount = assetReduction
    ? blocks.reduce((s, b) => s + (b.subsidyShare || 0), 0) : 0;
  const tranchesByYear = assetReduction ? subsidyTranchesByYear(caseData, sanctionedAmount) : {};

  return blocks.map(block => {
    // True opening balance is 0 — a block's Investment Amount is Year 1's
    // addition (the asset is acquired/constructed during the project), not
    // pre-existing opening WDV. Matches the CA's own sanction-report layout:
    // "Opening Balance -", then "Addition during the year" carries the full
    // investment in Year 1 only. Same convention as computeDepreciation in
    // dprEngine.js.
    let openingGross = 0;
    let openingDep = block.openingDep || 0;
    const isSlm = block.method === 'slm';

    const rows = Array.from({ length: projectionYears }, (_, idx) => {
      const y = idx + 1;
      const investmentAddition = y === 1 ? (block.openingGross || 0) : 0;
      const idcpAddition = y === 1 ? (block.idcpShare || 0) : 0;
      // Each tranche reduces this year's Building/P&M block in the ratio of
      // their own investment — the SAME year the money is actually received
      // (per its disbursement date), not lumped into Year 1.
      const yearTranche = tranchesByYear[y] || 0;
      const subsidyReduction = !yearTranche ? 0
        : block.sourceKey === 'buildingItems' ? round2(yearTranche * buildingRatio)
        : block.sourceKey === 'plantMachineryItems' ? round2(yearTranche * (1 - buildingRatio))
        : 0;
      const totalAdditionThisYear = investmentAddition + idcpAddition - subsidyReduction;

      const gross = openingGross + totalAdditionThisYear;
      const wdvOpening = gross - openingDep;
      const depBase = isSlm ? gross : wdvOpening;
      const depAccounting = round2(depBase * ((block.rate || 0) / 100));
      const closingDep = round2(openingDep + depAccounting);

      const row = {
        // "total" is the depreciable base this year before this year's
        // charge — WDV's is the reducing balance (gross - accumulated dep to
        // date), SLM's is the gross cost itself. Matches dprEngine.js.
        year: y, openingBalance: round2(openingGross), addition: round2(investmentAddition),
        additionIdcp: round2(idcpAddition), lessSubsidy: round2(subsidyReduction),
        total: round2(depBase), closingGross: round2(gross), depreciationAccounting: depAccounting, netBlockAccounting: round2(gross - closingDep),
      };
      openingGross = gross; openingDep = closingDep;
      return row;
    });

    return { name: block.name, rate: block.rate, method: block.method || 'wdv', sourceKey: block.sourceKey, rows };
  });
}
